import { useCallback } from 'react';

import type { Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import { Apollo } from 'src/gql/generated';

import type { WorkflowCardWorkflow } from './workflow-card/WorkflowCard';

/**
 * Minimal mutation for moving a workflow to a column.
 * Only returns the fields needed for cache updates.
 */
gql`
  mutation MoveWorkflowToColumn($input: UpdateWorkflowInput!) {
    updateWorkflow(input: $input) {
      workflow {
        __typename
        id
        column {
          __typename
          id
          category
          color
          name
        }
      }
    }
  }
`;

interface TargetColumn {
  category: Apollo.ColumnCategory;
  color: string;
  id: string;
  name: string;
}

interface MoveWorkflowParams {
  targetColumn: TargetColumn;
  workflow: WorkflowCardWorkflow;
  confirmUnpark?: boolean;
}

interface UseMoveWorkflowToColumnResult {
  isMoving: boolean;
  moveWorkflow: (params: MoveWorkflowParams) => Promise<boolean>;
}

/**
 * Hook to move a workflow to a different column with optimistic updates.
 *
 * Updates the Apollo cache directly to move the workflow between column query results,
 * providing instant visual feedback without requiring a refetch.
 *
 * Strategy:
 * 1. Remove workflow from the source column's list
 * 2. Add workflow to the target column's list
 * 3. Update the workflow entity itself (via optimisticResponse)
 */
export function useMoveWorkflowToColumn(): UseMoveWorkflowToColumnResult {
  const { cache } = useApolloClient();
  const [moveWorkflowMutation, { loading: isMoving }] = Apollo.useMoveWorkflowToColumnMutation();

  const moveWorkflow = useCallback(
    async ({
      confirmUnpark = false,
      targetColumn,
      workflow,
    }: MoveWorkflowParams): Promise<boolean> => {
      if (workflow.column?.id === targetColumn.id) return true;

      const workflowRef = cache.identify({ __typename: 'Workflow', id: workflow.id });
      const sourceColumnId = workflow.column?.id;

      await moveWorkflowMutation({
        optimisticResponse: {
          __typename: 'Mutation',
          updateWorkflow: {
            __typename: 'WorkflowResponse',
            workflow: {
              __typename: 'Workflow',
              column: {
                __typename: 'Column',
                category: targetColumn.category,
                color: targetColumn.color,
                id: targetColumn.id,
                name: targetColumn.name,
              },
              id: workflow.id,
            },
          },
        },
        update: () => {
          if (!workflowRef) return;

          // Step 1: Remove workflow from the source column's list
          cache.modify({
            fields: {
              workflows(existing, { readField, storeFieldName }) {
                if (!existing?.rows) return existing;
                if (
                  !sourceColumnId ||
                  !getWorkflowsFieldTargetsColumn(storeFieldName, sourceColumnId)
                )
                  return existing;

                return {
                  ...existing,
                  meta: {
                    ...existing.meta,
                    totalCount: Math.max(0, (existing.meta?.totalCount ?? 1) - 1),
                  },
                  rows: existing.rows.filter(
                    (ref: Reference) => readField('id', ref) !== workflow.id
                  ),
                };
              },
            },
          });

          // Step 2: Add workflow to the target column's list
          cache.modify({
            fields: {
              workflows(existing, { readField, storeFieldName }) {
                if (!getWorkflowsFieldTargetsColumn(storeFieldName, targetColumn.id))
                  return existing;

                const rows = existing?.rows ?? [];
                const hasWorkflow = rows.some(
                  (ref: Reference) => readField('id', ref) === workflow.id
                );
                if (hasWorkflow) return existing;

                return {
                  ...existing,
                  meta: {
                    ...existing?.meta,
                    totalCount: (existing?.meta?.totalCount ?? 0) + 1,
                  },
                  rows: [{ __ref: workflowRef }, ...rows],
                };
              },
            },
          });
        },
        variables: {
          input: {
            confirmUnpark,
            props: { columnId: targetColumn.id },
            workflowId: workflow.id,
          },
        },
      });

      return true;
    },
    [cache, moveWorkflowMutation]
  );

  return { isMoving, moveWorkflow };
}

function getWorkflowsFieldTargetsColumn(storeFieldName: string, columnId: string) {
  return (
    storeFieldName.includes('"field":"column"') && storeFieldName.includes(`"id":"${columnId}"`)
  );
}
